/**
 * CommandCenterLayout — Sprint 21.7.2
 *
 * Shared template for every Domain Command Center (VISION §5.5, ADR-027).
 * Consumes /api/command/:domain/overview, renders consistent blocks:
 *
 *   Header · Summary strip · KAIROS framing · Signals · Your exposure /
 *   Watchlist candidates · Catalysts / Theme exposure · Recent alerts.
 *
 * Per ADR-028, this component is PURE RENDERING. It fetches a single API
 * endpoint and lays out the result. No theses, no LLM calls, no inline
 * classification. If a field is missing, a muted placeholder is shown —
 * the layout never does the thinking.
 *
 * Mobile responsive by design: desktop uses two-column paired blocks,
 * mobile stacks everything single-column with reduced spacing. All
 * tappable targets ≥ 44x44 px.
 */

import { useEffect, useState } from 'react';
import { Clock, TrendingUp, TrendingDown, Minus, ExternalLink, Zap, Target, Calendar, Layers, Bell, AlertTriangle, Landmark } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const DIRECTION_COLOR = {
  bullish: '#10b981',
  up: '#10b981',
  bearish: '#ef4444',
  down: '#ef4444',
  neutral: '#6b7280',
  flat: '#6b7280',
  mixed: '#f59e0b',
};
const DIRECTION_ICON = {
  bullish: TrendingUp, up: TrendingUp,
  bearish: TrendingDown, down: TrendingDown,
  neutral: Minus, flat: Minus, mixed: Minus,
};

function DirectionBadge({ direction, size = 'sm' }) {
  if (!direction) return null;
  const color = DIRECTION_COLOR[direction] || '#6b7280';
  const Icon = DIRECTION_ICON[direction] || Minus;
  const cls = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-mono font-bold uppercase" style={{ color }}>
      <Icon className={cls} /> {direction}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = Date.now();
  const ageMs = now - d.getTime();
  if (ageMs < 60_000) return 'just now';
  if (ageMs < 3_600_000) return `${Math.floor(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000) return `${Math.floor(ageMs / 3_600_000)}h ago`;
  return d.toISOString().slice(0, 10);
}

function fmtEventDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

// ─── Blocks ──────────────────────────────────────────────────────────────

function SummaryStrip({ strip }) {
  if (!strip) return null;
  const items = [
    { label: 'Regime', value: strip.regime },
    { label: 'Today', value: strip.direction_today },
    { label: 'Notable', value: strip.notable_move },
    { label: 'Volatility', value: strip.volatility_state },
  ].filter(x => x.value);

  if (items.length === 0) {
    return (
      <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3 text-[10px] font-mono text-korinth-muted italic">
        Summary strip unavailable.
      </div>
    );
  }

  return (
    <div className="bg-korinth-surface border border-korinth-border rounded-lg px-3 py-2 flex flex-wrap gap-3 md:gap-5">
      {items.map((x, i) => (
        <div key={i} className="flex flex-col min-w-0">
          <span className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">{x.label}</span>
          <span className="text-xs font-mono text-white truncate">{x.value}</span>
        </div>
      ))}
    </div>
  );
}

function KairosFraming({ framing, domain, fallbackInFlight }) {
  if (!framing) return null;

  // Sprint 21.7.2 followup #2: if the primary overview returned a stub
  // AND the fallback fetch is still in flight, show a pulsing skeleton
  // instead of the "unavailable" fallback — user sees movement, not error.
  if ((framing._stub || !framing.synthesis_text) && fallbackInFlight) {
    return (
      <div className="bg-korinth-surface border-l-4 border-l-korinth-accent border border-korinth-border rounded-lg p-3 md:p-4">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-3.5 h-3.5 text-korinth-accent animate-pulse" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-korinth-accent">KAIROS Framing · {domain}</span>
          <span className="text-[9px] font-mono text-korinth-muted">loading…</span>
        </div>
        <div className="space-y-2 animate-pulse">
          <div className="h-4 bg-korinth-border/50 rounded w-3/4" />
          <div className="h-3 bg-korinth-border/30 rounded w-full" />
          <div className="h-3 bg-korinth-border/30 rounded w-5/6" />
        </div>
      </div>
    );
  }

  // Genuine unavailable state — primary stubbed AND fallback also failed
  // (or hasn't been kicked off, e.g., stale component).
  if (framing._stub || !framing.synthesis_text) {
    return (
      <div className="bg-korinth-surface border-l-4 border-l-korinth-muted border border-korinth-border rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-3.5 h-3.5 text-korinth-muted" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-korinth-muted">KAIROS Framing</span>
        </div>
        <p className="text-[11px] font-mono text-korinth-muted italic">
          {framing._reason === 'no_agent_output_yet'
            ? 'Framing unavailable — waiting on first KAIROS cycle with cross-asset context.'
            : 'Framing temporarily unavailable.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-korinth-surface border-l-4 border-l-korinth-accent border border-korinth-border rounded-lg p-3 md:p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Zap className="w-3.5 h-3.5 text-korinth-accent" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-korinth-accent">KAIROS Framing · {domain}</span>
        {framing.conviction != null && (
          <span className="text-[9px] font-mono text-korinth-muted">conviction {framing.conviction}%</span>
        )}
      </div>
      {framing.headline && (
        <div className="text-sm md:text-base font-bold text-white leading-snug mb-1.5">{framing.headline}</div>
      )}
      <p className="text-[11px] md:text-xs font-mono text-korinth-text leading-relaxed whitespace-pre-line">
        {framing.synthesis_text}
      </p>
      {(framing.cross_domain_notes || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {framing.cross_domain_notes.map((n, i) => {
            const color = DIRECTION_COLOR[n.direction] || '#6b7280';
            return (
              <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded border text-[9px] md:text-[10px] font-mono"
                   style={{ borderColor: color + '40', background: color + '10' }}>
                <span className="uppercase tracking-wider text-korinth-muted">{n.domain}</span>
                <span className="text-korinth-text">{n.note}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MONETARY REGIME BLOCK (Sprint 21.9, macro domain only) ──────────────
//
// Renders the MONETARIS specialist's latest stance: regime, confidence,
// key drivers (top 3, expandable), Fed path arrow, portfolio impact, and
// a one-paragraph "MONETARIS take" narrative. Color-coded by regime:
// hawkish=red, neutral=gray, dovish=green. Staleness tag appears if the
// stance is >6h old (cadence is 4h, so >6h = missed at least one cron).

const REGIME_STYLE = {
  hawkish: { color: '#ef4444', bg: 'bg-korinth-danger/10', border: 'border-korinth-danger/40', label: 'HAWKISH' },
  neutral: { color: '#9ca3af', bg: 'bg-korinth-surface-2', border: 'border-korinth-border', label: 'NEUTRAL' },
  dovish:  { color: '#10b981', bg: 'bg-korinth-success/10', border: 'border-korinth-success/40', label: 'DOVISH' },
};

function MonetaryRegimeBlock({ ctx }) {
  const [expanded, setExpanded] = useState(false);
  if (!ctx) return null;
  if (ctx.available === false) {
    return (
      <ModuleContainer title="Monetary Regime" subtitle="MONETARIS specialist output" icon={<Landmark className="w-3.5 h-3.5" />}>
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">
          {ctx.reason || 'MONETARIS has not produced a stance yet.'}
        </p>
      </ModuleContainer>
    );
  }

  const regime = (ctx.overall_regime || 'neutral').toLowerCase();
  const style = REGIME_STYLE[regime] || REGIME_STYLE.neutral;
  const drivers = Array.isArray(ctx.key_drivers) ? ctx.key_drivers : [];
  const visibleDrivers = expanded ? drivers : drivers.slice(0, 3);
  const fedPath = ctx.fed_path_next_6mo || null;
  const isStale = Number.isFinite(ctx.staleness_hours) && ctx.staleness_hours > 6;

  return (
    <ModuleContainer
      title="Monetary Regime"
      subtitle="MONETARIS specialist · central bank + rate path"
      icon={<Landmark className="w-3.5 h-3.5" />}
    >
      <div className={`rounded-lg border ${style.border} ${style.bg} p-3 space-y-3`}>
        {/* Top row: regime + confidence */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div
              className="px-2.5 py-1 rounded text-[11px] font-bold font-mono"
              style={{ color: style.color, backgroundColor: `${style.color}22`, border: `1px solid ${style.color}55` }}
            >
              {style.label}
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-muted">
              <span>confidence</span>
              <div className="w-20 h-1.5 bg-korinth-border rounded overflow-hidden">
                <div className="h-full rounded" style={{ width: `${ctx.confidence || 0}%`, backgroundColor: style.color }} />
              </div>
              <span className="text-white">{ctx.confidence || 0}%</span>
            </div>
          </div>
          {isStale && (
            <div className="text-[9px] font-mono text-korinth-warning flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> stale {ctx.staleness_hours}h
            </div>
          )}
        </div>

        {/* Fed path */}
        {fedPath && (
          <div className="flex items-center gap-2 text-[11px] font-mono">
            <span className="text-korinth-muted">Fed path 6mo:</span>
            <span className="text-white font-bold">
              {fedPath.direction === 'up' ? '▲' : fedPath.direction === 'down' ? '▼' : '◆'}
              {' '}{fedPath.terminal_rate_pct ?? '?'}%
            </span>
            {Number.isFinite(fedPath.probability) && (
              <span className="text-korinth-muted">(p={fedPath.probability}%)</span>
            )}
            {fedPath.rationale && (
              <span className="text-korinth-muted italic hidden md:inline">· {fedPath.rationale}</span>
            )}
          </div>
        )}

        {/* Curve shape + transition probability */}
        <div className="flex items-center gap-3 text-[10px] font-mono flex-wrap">
          {ctx.curve_shape && (
            <span>
              <span className="text-korinth-muted">Curve:</span>{' '}
              <span className="text-white">{ctx.curve_shape}</span>
            </span>
          )}
          {Number.isFinite(ctx.regime_transition_probability) && (
            <span>
              <span className="text-korinth-muted">Transition prob:</span>{' '}
              <span className="text-white">{ctx.regime_transition_probability}%</span>
            </span>
          )}
        </div>

        {/* Key drivers */}
        {drivers.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-mono text-korinth-muted">Key drivers</div>
            <ul className="space-y-0.5">
              {visibleDrivers.map((d, i) => (
                <li key={i} className="text-[11px] text-white font-mono leading-snug">
                  <span className="text-korinth-muted">·</span> {d}
                </li>
              ))}
            </ul>
            {drivers.length > 3 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-[10px] font-mono text-korinth-accent hover:text-white transition"
              >
                {expanded ? 'show less' : `+${drivers.length - 3} more`}
              </button>
            )}
          </div>
        )}

        {/* Portfolio impact */}
        {ctx.portfolio_impact_summary && (
          <div className="pt-2 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">Impact on your book</div>
            <p className="text-[11px] text-white leading-relaxed">{ctx.portfolio_impact_summary}</p>
          </div>
        )}

        {/* MONETARIS take narrative */}
        {ctx.monetaris_take && (
          <div className="pt-2 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">MONETARIS take</div>
            <p className="text-[11px] text-korinth-muted italic leading-relaxed">{ctx.monetaris_take}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-[9px] font-mono text-korinth-muted pt-1">
          <span>as of {fmtTime(ctx.as_of)}</span>
          <span>trigger: {ctx.cycle_trigger || 'cron'}</span>
        </div>
      </div>
    </ModuleContainer>
  );
}

// ─── EQUITY REGIME BLOCK (Sprint 21.10, equity domain only) ─────────────
//
// Renders the AGORIS specialist's latest stance: regime (risk_on /
// neutral / risk_off / rotation), leading + lagging sectors with rel-strength
// bars, factor tilt badges, earnings window for Kamal's book, portfolio
// fit indicator, 0-3 actionable candidates, and the agoris_take narrative.
// Color-coded by regime: risk_on green, neutral gray, risk_off red,
// rotation amber. Staleness tag if >6h old.

const EQUITY_REGIME_STYLE = {
  risk_on:  { color: '#10b981', bg: 'bg-korinth-success/10', border: 'border-korinth-success/40', label: 'RISK-ON' },
  neutral:  { color: '#9ca3af', bg: 'bg-korinth-surface-2', border: 'border-korinth-border', label: 'NEUTRAL' },
  risk_off: { color: '#ef4444', bg: 'bg-korinth-danger/10', border: 'border-korinth-danger/40', label: 'RISK-OFF' },
  rotation: { color: '#f59e0b', bg: 'bg-korinth-warning/10', border: 'border-korinth-warning/40', label: 'ROTATION' },
};

const PORTFOLIO_FIT_STYLE = {
  aligned:             { color: '#10b981', label: 'ALIGNED' },
  partially_aligned:   { color: '#f59e0b', label: 'PARTIAL' },
  misaligned:          { color: '#ef4444', label: 'MISALIGNED' },
};

function RelStrengthBar({ pct }) {
  if (pct == null || !Number.isFinite(pct)) return <span className="text-korinth-muted text-[10px]">n/a</span>;
  const magnitude = Math.min(Math.abs(pct), 20); // cap display at ±20pp
  const widthPct = (magnitude / 20) * 50;
  const isPositive = pct >= 0;
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-24 h-1.5 bg-korinth-border rounded overflow-hidden relative">
        <div className="absolute inset-y-0 left-1/2 w-px bg-korinth-muted/40" />
        <div
          className="absolute inset-y-0 rounded"
          style={{
            backgroundColor: isPositive ? '#10b981' : '#ef4444',
            left: isPositive ? '50%' : `${50 - widthPct}%`,
            width: `${widthPct}%`,
          }}
        />
      </div>
      <span className={`text-[10px] font-mono ${isPositive ? 'text-korinth-success' : 'text-korinth-danger'}`}>
        {isPositive ? '+' : ''}{pct.toFixed(1)}pp
      </span>
    </div>
  );
}

function EquityRegimeBlock({ ctx }) {
  const [expandedCandidates, setExpandedCandidates] = useState(false);
  if (!ctx) return null;
  if (ctx.available === false) {
    return (
      <ModuleContainer title="Equity Regime" subtitle="AGORIS specialist output">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">
          {ctx.reason || 'AGORIS has not produced a stance yet.'}
        </p>
      </ModuleContainer>
    );
  }

  const regime = (ctx.overall_regime || 'neutral').toLowerCase();
  const style = EQUITY_REGIME_STYLE[regime] || EQUITY_REGIME_STYLE.neutral;
  const leading = Array.isArray(ctx.leading_sectors) ? ctx.leading_sectors : [];
  const lagging = Array.isArray(ctx.lagging_sectors) ? ctx.lagging_sectors : [];
  const earnings = Array.isArray(ctx.earnings_focus) ? ctx.earnings_focus : [];
  const candidates = Array.isArray(ctx.actionable_candidates) ? ctx.actionable_candidates : [];
  const fit = (ctx.portfolio_fit || 'partially_aligned').toLowerCase();
  const fitStyle = PORTFOLIO_FIT_STYLE[fit] || PORTFOLIO_FIT_STYLE.partially_aligned;
  const isStale = Number.isFinite(ctx.staleness_hours) && ctx.staleness_hours > 6;
  const tilt = ctx.factor_tilt || null;
  const visibleCandidates = expandedCandidates ? candidates : candidates.slice(0, 2);

  return (
    <ModuleContainer
      title="Equity Regime"
      subtitle="AGORIS specialist · sector rotation + factor tilt + earnings"
    >
      <div className={`rounded-lg border ${style.border} ${style.bg} p-3 space-y-3`}>
        {/* Top row: regime + confidence + portfolio fit */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div
              className="px-2.5 py-1 rounded text-[11px] font-bold font-mono"
              style={{ color: style.color, backgroundColor: `${style.color}22`, border: `1px solid ${style.color}55` }}
            >
              {style.label}
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-muted">
              <span>confidence</span>
              <div className="w-20 h-1.5 bg-korinth-border rounded overflow-hidden">
                <div className="h-full rounded" style={{ width: `${ctx.confidence || 0}%`, backgroundColor: style.color }} />
              </div>
              <span className="text-white">{ctx.confidence || 0}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="px-2 py-0.5 rounded text-[10px] font-mono font-bold"
              style={{ color: fitStyle.color, backgroundColor: `${fitStyle.color}22`, border: `1px solid ${fitStyle.color}55` }}
            >
              fit: {fitStyle.label}
            </div>
            {isStale && (
              <div className="text-[9px] font-mono text-korinth-warning flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> stale {ctx.staleness_hours}h
              </div>
            )}
          </div>
        </div>

        {/* Leading sectors */}
        {leading.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-mono text-korinth-muted">Leading sectors (20d rel SPY)</div>
            <div className="space-y-1">
              {leading.slice(0, 3).map((s, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono font-bold text-white w-10">{s.sector_code}</span>
                  <RelStrengthBar pct={s.relative_strength_pct} />
                  <span className="text-[10px] text-korinth-muted flex-1 min-w-0 truncate">{s.rationale || ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lagging sectors */}
        {lagging.length > 0 && (
          <div className="space-y-1">
            <div className="text-[10px] font-mono text-korinth-muted">Lagging sectors (20d rel SPY)</div>
            <div className="space-y-1">
              {lagging.slice(0, 3).map((s, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-mono font-bold text-white w-10">{s.sector_code}</span>
                  <RelStrengthBar pct={s.relative_strength_pct} />
                  <span className="text-[10px] text-korinth-muted flex-1 min-w-0 truncate">{s.rationale || ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Factor tilt */}
        {tilt && (
          <div className="flex items-center gap-2 text-[10px] font-mono flex-wrap pt-1 border-t border-korinth-border">
            <span className="text-korinth-muted">Factor tilt:</span>
            {tilt.growth_vs_value && <span className="px-1.5 py-0.5 rounded bg-korinth-surface-2 text-white">{tilt.growth_vs_value.replace(/_/g, ' ')}</span>}
            {tilt.large_vs_small && <span className="px-1.5 py-0.5 rounded bg-korinth-surface-2 text-white">{tilt.large_vs_small.replace(/_/g, ' ')}</span>}
            {tilt.quality_tilt && <span className="px-1.5 py-0.5 rounded bg-korinth-surface-2 text-white">{tilt.quality_tilt.replace(/_/g, ' ')}</span>}
          </div>
        )}

        {/* Earnings focus */}
        {earnings.length > 0 && (
          <div className="space-y-1 pt-1 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted">Earnings window (next 7d · your book)</div>
            <ul className="space-y-0.5">
              {earnings.slice(0, 5).map((e, i) => {
                const volColor = e.volatility_expectation === 'high' ? '#ef4444' : e.volatility_expectation === 'medium' ? '#f59e0b' : '#9ca3af';
                return (
                  <li key={i} className="text-[11px] font-mono flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold w-14">{e.ticker}</span>
                    <span className="text-korinth-muted">{e.event_date}</span>
                    <span style={{ color: volColor }}>{e.volatility_expectation || '?'} vol</span>
                    {e.note && <span className="text-korinth-muted text-[10px] truncate flex-1 min-w-0">{e.note}</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Portfolio fit reason */}
        {ctx.portfolio_fit_reason && (
          <div className="pt-1 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">Portfolio fit</div>
            <p className="text-[11px] text-white leading-relaxed">{ctx.portfolio_fit_reason}</p>
          </div>
        )}

        {/* Actionable candidates (if any) */}
        {candidates.length > 0 && (
          <div className="pt-1 border-t border-korinth-border space-y-1.5">
            <div className="text-[10px] font-mono text-korinth-muted flex items-center gap-1">
              <Target className="w-3 h-3" /> Actionable candidates ({candidates.length}, ≥75% conviction)
            </div>
            {visibleCandidates.map((c, i) => {
              const dirColor = c.direction === 'buy' ? '#10b981' : c.direction === 'trim' || c.direction === 'short' ? '#ef4444' : '#9ca3af';
              return (
                <div key={i} className="rounded border border-korinth-border p-2 bg-korinth-surface-2 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-mono font-bold text-white">{c.ticker}</span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold"
                      style={{ color: dirColor, backgroundColor: `${dirColor}22`, border: `1px solid ${dirColor}55` }}
                    >
                      {(c.direction || '').toUpperCase()}
                    </span>
                    <span className="text-[10px] font-mono text-korinth-muted">{c.conviction}% conf</span>
                    {Number.isFinite(c.size_hint_pct) && (
                      <span className="text-[10px] font-mono text-korinth-muted">size {c.size_hint_pct}%</span>
                    )}
                  </div>
                  {c.thesis && <p className="text-[10px] text-white leading-relaxed">{c.thesis}</p>}
                  {c.invalidation && (
                    <p className="text-[10px] text-korinth-muted italic">× if: {c.invalidation}</p>
                  )}
                </div>
              );
            })}
            {candidates.length > 2 && (
              <button
                onClick={() => setExpandedCandidates(!expandedCandidates)}
                className="text-[10px] font-mono text-korinth-accent hover:text-white transition"
              >
                {expandedCandidates ? 'show less' : `+${candidates.length - 2} more`}
              </button>
            )}
          </div>
        )}

        {/* AGORIS take narrative */}
        {ctx.agoris_take && (
          <div className="pt-1 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">AGORIS take</div>
            <p className="text-[11px] text-korinth-muted italic leading-relaxed">{ctx.agoris_take}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-[9px] font-mono text-korinth-muted pt-1">
          <span>as of {fmtTime(ctx.as_of)} · session: {ctx.session_phase || '?'}</span>
          <span>trigger: {ctx.cycle_trigger || 'cron'}</span>
        </div>
      </div>
    </ModuleContainer>
  );
}

// ─── VOLATILITY REGIME BLOCK (Sprint 21.11, options domain only) ────────
//
// Renders the JANUS specialist's latest stance. Regime badge (complacent
// green / normalizing gray / elevated amber / panic red), VIX level +
// percentile + term-structure chip, SKEW / VVIX compact stats,
// implied-vs-realized gauge, cross-regime divergences list, portfolio
// vol-exposure indicator, 0-3 actionable candidates, JANUS narrative.

const VOL_REGIME_STYLE = {
  complacent:   { color: '#10b981', bg: 'bg-korinth-success/10', border: 'border-korinth-success/40', label: 'COMPLACENT' },
  normalizing:  { color: '#9ca3af', bg: 'bg-korinth-surface-2', border: 'border-korinth-border', label: 'NORMALIZING' },
  elevated:     { color: '#f59e0b', bg: 'bg-korinth-warning/10', border: 'border-korinth-warning/40', label: 'ELEVATED' },
  panic:        { color: '#ef4444', bg: 'bg-korinth-danger/10', border: 'border-korinth-danger/40', label: 'PANIC' },
};

const VOL_EXPOSURE_STYLE = {
  high_sensitivity: { color: '#ef4444', label: 'HIGH SENSITIVITY' },
  moderate:         { color: '#f59e0b', label: 'MODERATE' },
  low_sensitivity:  { color: '#10b981', label: 'LOW SENSITIVITY' },
  unknown:          { color: '#9ca3af', label: 'UNKNOWN' },
};

const TERM_STRUCTURE_STYLE = {
  contango:       { color: '#10b981', label: 'contango' },
  flat:           { color: '#9ca3af', label: 'flat' },
  backwardation:  { color: '#ef4444', label: 'backwardation' },
  unknown:        { color: '#6b7280', label: 'unknown' },
};

const DIVERGENCE_SEVERITY_COLOR = { low: '#9ca3af', medium: '#f59e0b', high: '#ef4444' };

function VolStat({ label, value, pct, suffix = '' }) {
  const display = value != null && Number.isFinite(value)
    ? `${typeof value === 'number' ? Math.round(value * 100) / 100 : value}${suffix}`
    : 'n/a';
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-mono text-korinth-muted">{label}</span>
      <span className="text-[11px] font-mono text-white font-bold">{display}</span>
      {Number.isFinite(pct) && (
        <span className="text-[9px] font-mono text-korinth-muted">p{pct}</span>
      )}
    </div>
  );
}

function VolatilityRegimeBlock({ ctx }) {
  const [expandedCandidates, setExpandedCandidates] = useState(false);
  if (!ctx) return null;
  if (ctx.available === false) {
    return (
      <ModuleContainer title="Volatility Regime" subtitle="JANUS specialist output">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">
          {ctx.reason || 'JANUS has not produced a stance yet.'}
        </p>
      </ModuleContainer>
    );
  }

  const regime = (ctx.overall_regime || 'normalizing').toLowerCase();
  const style = VOL_REGIME_STYLE[regime] || VOL_REGIME_STYLE.normalizing;
  const termKey = (ctx.vix_term_structure || 'unknown').toLowerCase();
  const termStyle = TERM_STRUCTURE_STYLE[termKey] || TERM_STRUCTURE_STYLE.unknown;
  const exposure = (ctx.portfolio_vol_exposure || 'unknown').toLowerCase();
  const expStyle = VOL_EXPOSURE_STYLE[exposure] || VOL_EXPOSURE_STYLE.unknown;
  const divergences = Array.isArray(ctx.cross_regime_divergences) ? ctx.cross_regime_divergences : [];
  const candidates = Array.isArray(ctx.actionable_candidates) ? ctx.actionable_candidates : [];
  const visibleCandidates = expandedCandidates ? candidates : candidates.slice(0, 2);
  const isStale = Number.isFinite(ctx.staleness_hours) && ctx.staleness_hours > 6;

  return (
    <ModuleContainer
      title="Volatility Regime"
      subtitle="JANUS specialist · VIX term structure + cross-regime divergences"
    >
      <div className={`rounded-lg border ${style.border} ${style.bg} p-3 space-y-3`}>
        {/* Top row: regime + confidence + exposure */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div
              className="px-2.5 py-1 rounded text-[11px] font-bold font-mono"
              style={{ color: style.color, backgroundColor: `${style.color}22`, border: `1px solid ${style.color}55` }}
            >
              {style.label}
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-muted">
              <span>confidence</span>
              <div className="w-20 h-1.5 bg-korinth-border rounded overflow-hidden">
                <div className="h-full rounded" style={{ width: `${ctx.confidence || 0}%`, backgroundColor: style.color }} />
              </div>
              <span className="text-white">{ctx.confidence || 0}%</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="px-2 py-0.5 rounded text-[10px] font-mono font-bold"
              style={{ color: expStyle.color, backgroundColor: `${expStyle.color}22`, border: `1px solid ${expStyle.color}55` }}
            >
              book: {expStyle.label}
            </div>
            {isStale && (
              <div className="text-[9px] font-mono text-korinth-warning flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> stale {ctx.staleness_hours}h
              </div>
            )}
          </div>
        </div>

        {/* VIX term structure row */}
        <div className="flex items-center gap-4 flex-wrap pt-1 border-t border-korinth-border">
          <VolStat label="VIX" value={ctx.vix_level} pct={ctx.vix_percentile_252d} />
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-mono text-korinth-muted">term:</span>
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold"
              style={{ color: termStyle.color, backgroundColor: `${termStyle.color}22` }}
            >
              {termStyle.label}
            </span>
            {Number.isFinite(ctx.term_structure_spread) && (
              <span className="text-[10px] font-mono text-korinth-muted">
                ({ctx.term_structure_spread >= 0 ? '+' : ''}{ctx.term_structure_spread})
              </span>
            )}
          </div>
          <VolStat label="SKEW" value={ctx.skew_level} pct={ctx.skew_percentile_252d} />
          <VolStat label="VVIX" value={ctx.vvix_level} />
          {ctx.vvix_regime && (
            <span className="text-[10px] font-mono text-korinth-muted">({ctx.vvix_regime})</span>
          )}
        </div>

        {/* Realized vs implied */}
        {(Number.isFinite(ctx.realized_vol_20d) || Number.isFinite(ctx.iv_rv_spread)) && (
          <div className="flex items-center gap-4 flex-wrap text-[10px] font-mono">
            <span>
              <span className="text-korinth-muted">RV20 (SPY):</span>{' '}
              <span className="text-white">{Number.isFinite(ctx.realized_vol_20d) ? `${ctx.realized_vol_20d.toFixed(2)}%` : 'n/a'}</span>
            </span>
            {Number.isFinite(ctx.iv_rv_spread) && (
              <span>
                <span className="text-korinth-muted">IV–RV spread:</span>{' '}
                <span className={ctx.iv_rv_spread >= 0 ? 'text-korinth-muted' : 'text-korinth-danger'}>
                  {ctx.iv_rv_spread >= 0 ? '+' : ''}{ctx.iv_rv_spread}
                </span>
              </span>
            )}
          </div>
        )}

        {/* Cross-regime divergences */}
        {divergences.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted">Cross-regime divergences</div>
            <ul className="space-y-0.5">
              {divergences.slice(0, 3).map((d, i) => {
                const sevColor = DIVERGENCE_SEVERITY_COLOR[(d.severity || 'low').toLowerCase()] || '#9ca3af';
                return (
                  <li key={i} className="text-[11px] font-mono flex items-start gap-2">
                    <span
                      className="px-1 py-0.5 rounded text-[9px] font-bold mt-0.5"
                      style={{ color: sevColor, backgroundColor: `${sevColor}22` }}
                    >
                      {(d.severity || 'low').toUpperCase()}
                    </span>
                    <span className="text-white leading-snug">{d.description || d.type}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Portfolio vol exposure reason */}
        {ctx.portfolio_vol_reason && (
          <div className="pt-1 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">Book vol sensitivity</div>
            <p className="text-[11px] text-white leading-relaxed">{ctx.portfolio_vol_reason}</p>
          </div>
        )}

        {/* Gamma exposure read (narrative) */}
        {ctx.gamma_exposure_read && (
          <div className="pt-1 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">Gamma exposure read</div>
            <p className="text-[11px] text-korinth-muted italic leading-relaxed">{ctx.gamma_exposure_read}</p>
          </div>
        )}

        {/* Actionable candidates */}
        {candidates.length > 0 && (
          <div className="pt-1 border-t border-korinth-border space-y-1.5">
            <div className="text-[10px] font-mono text-korinth-muted flex items-center gap-1">
              <Target className="w-3 h-3" /> Actionable candidates ({candidates.length}, ≥75% conviction)
            </div>
            {visibleCandidates.map((c, i) => {
              const urgencyColor = c.urgency === 'now' ? '#ef4444' : c.urgency === 'within_48h' ? '#f59e0b' : '#9ca3af';
              return (
                <div key={i} className="rounded border border-korinth-border p-2 bg-korinth-surface-2 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-mono font-bold text-white">
                      {(c.action || '').replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold"
                      style={{ color: urgencyColor, backgroundColor: `${urgencyColor}22`, border: `1px solid ${urgencyColor}55` }}
                    >
                      {(c.urgency || 'monitor').toUpperCase()}
                    </span>
                    <span className="text-[10px] font-mono text-korinth-muted">{c.conviction}% conf</span>
                  </div>
                  {c.rationale && <p className="text-[10px] text-white leading-relaxed">{c.rationale}</p>}
                  {c.invalidation && (
                    <p className="text-[10px] text-korinth-muted italic">× if: {c.invalidation}</p>
                  )}
                </div>
              );
            })}
            {candidates.length > 2 && (
              <button
                onClick={() => setExpandedCandidates(!expandedCandidates)}
                className="text-[10px] font-mono text-korinth-accent hover:text-white transition"
              >
                {expandedCandidates ? 'show less' : `+${candidates.length - 2} more`}
              </button>
            )}
          </div>
        )}

        {/* JANUS take narrative */}
        {ctx.janus_take && (
          <div className="pt-1 border-t border-korinth-border">
            <div className="text-[10px] font-mono text-korinth-muted mb-1">JANUS take</div>
            <p className="text-[11px] text-korinth-muted italic leading-relaxed">{ctx.janus_take}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-[9px] font-mono text-korinth-muted pt-1">
          <span>as of {fmtTime(ctx.as_of)} · session: {ctx.session_phase || '?'}</span>
          <span>trigger: {ctx.cycle_trigger || 'cron'}</span>
        </div>
      </div>
    </ModuleContainer>
  );
}

function SignalsBlock({ signals }) {
  if (!signals || signals.length === 0) {
    return (
      <ModuleContainer title="Signals (last 24h)" subtitle="Domain alerts and agent outputs">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">No signals in last 24h.</p>
      </ModuleContainer>
    );
  }
  return (
    <ModuleContainer title={`Signals (${signals.length})`} subtitle="Domain alerts and agent outputs · last 24h">
      <div className="space-y-2">
        {signals.map(s => {
          const color = DIRECTION_COLOR[s.direction] || '#6b7280';
          return (
            <a key={s.id} href={s.deep_link || '#'} className="block border border-korinth-border rounded-lg p-3 hover:border-korinth-accent/50 transition-colors active:bg-korinth-bg" style={{ minHeight: 44 }}>
              <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-korinth-muted">{s.source_agent}</span>
                  <DirectionBadge direction={s.direction} />
                  {s.conviction != null && <span className="text-[9px] font-mono text-korinth-muted">c{s.conviction}</span>}
                </div>
                <span className="text-[9px] font-mono text-korinth-muted flex-shrink-0">{fmtTime(s.created_at)}</span>
              </div>
              <div className="text-xs md:text-sm font-mono font-bold text-white mb-1">{s.headline}</div>
              {s.thesis && (
                <p className="text-[10px] md:text-[11px] font-mono text-korinth-text leading-relaxed line-clamp-3">{s.thesis}</p>
              )}
              {(s.affected_tickers || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {s.affected_tickers.slice(0, 6).map(t => (
                    <span key={t} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg text-korinth-accent border border-korinth-accent/20">{t}</span>
                  ))}
                </div>
              )}
            </a>
          );
        })}
      </div>
    </ModuleContainer>
  );
}

function YourExposureBlock({ exposure }) {
  if (!exposure) return null;
  if (!exposure.has_positions) {
    return (
      <ModuleContainer title="Your Exposure" subtitle="Discretionary + crypto holdings in this domain">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">
          {exposure.note_if_empty || 'No holdings in this domain.'}
        </p>
      </ModuleContainer>
    );
  }
  return (
    <ModuleContainer title="Your Exposure" subtitle={`${exposure.position_count} position${exposure.position_count === 1 ? '' : 's'} · ${exposure.total_weight_pct?.toFixed(0) || 0}% of domain`}>
      <div className="space-y-1">
        {(exposure.top_positions || []).map(p => (
          <div key={p.ticker} className="flex items-center gap-2 text-[11px] font-mono py-1.5 border-b border-korinth-border/30 last:border-0">
            <span className="font-bold text-white w-16 flex-shrink-0">{p.ticker}</span>
            <div className="flex-1 h-1.5 bg-korinth-border/30 rounded overflow-hidden">
              <div className="h-full bg-korinth-accent" style={{ width: `${Math.min(100, p.weight_pct || 0)}%` }} />
            </div>
            <span className="text-korinth-text w-14 text-right">{(p.weight_pct || 0).toFixed(1)}%</span>
            <DirectionBadge direction={p.direction_1d || p.direction_7d} />
          </div>
        ))}
      </div>
    </ModuleContainer>
  );
}

function WatchlistCandidatesBlock({ candidates }) {
  if (!candidates || candidates.length === 0) {
    return (
      <ModuleContainer title="Watchlist Candidates" subtitle="Active watchlist entries in this domain">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">No candidates in this domain.</p>
      </ModuleContainer>
    );
  }
  return (
    <ModuleContainer title="Watchlist Candidates" subtitle={`${candidates.length} active · not currently held`}>
      <div className="space-y-1.5">
        {candidates.map(c => (
          <div key={c.ticker} className="flex flex-col gap-1 py-1.5 border-b border-korinth-border/30 last:border-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-mono font-bold text-white">{c.ticker}</span>
              <span className="text-[9px] font-mono uppercase tracking-wider text-korinth-muted">{c.tier}</span>
              {(c.themes || []).slice(0, 2).map(th => (
                <span key={th} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg text-korinth-muted border border-korinth-border">{th}</span>
              ))}
            </div>
            {c.last_kairos_note && (
              <p className="text-[10px] font-mono text-korinth-text leading-snug line-clamp-2">{c.last_kairos_note}</p>
            )}
          </div>
        ))}
      </div>
    </ModuleContainer>
  );
}

function CatalystsBlock({ catalysts }) {
  if (!catalysts || catalysts.length === 0) {
    return (
      <ModuleContainer title="Catalysts · next 30d" subtitle="Upcoming events in this domain">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">No upcoming catalysts.</p>
      </ModuleContainer>
    );
  }
  return (
    <ModuleContainer title="Catalysts · next 30d" subtitle={`${catalysts.length} event${catalysts.length === 1 ? '' : 's'}`}>
      <div className="space-y-1">
        {catalysts.map((c, i) => (
          <div key={`${c.ticker || 'macro'}-${c.event_date}-${i}`} className="flex items-center gap-2 text-[10px] md:text-[11px] font-mono py-1.5 border-b border-korinth-border/30 last:border-0">
            <span className="font-bold text-white w-16 flex-shrink-0 truncate">{c.ticker || 'MACRO'}</span>
            <span className="text-korinth-text uppercase tracking-wider text-[9px] w-20 flex-shrink-0 truncate">{c.event_type}</span>
            <span className="text-korinth-muted flex-1 truncate">{fmtEventDate(c.event_date)}</span>
            {c.days_until != null && (
              <span className="text-[9px] font-mono text-korinth-accent flex-shrink-0">T-{c.days_until}d</span>
            )}
            {c.importance >= 3 && (
              <span className="text-[9px] font-bold text-korinth-danger flex-shrink-0">HIGH</span>
            )}
          </div>
        ))}
      </div>
    </ModuleContainer>
  );
}

function ThemeExposureBlock({ themes }) {
  if (!themes || themes.length === 0) {
    return (
      <ModuleContainer title="Theme Exposure" subtitle="Your invested themes in this domain">
        <p className="text-[10px] font-mono text-korinth-muted italic text-center py-3">No theme data yet.</p>
      </ModuleContainer>
    );
  }
  return (
    <ModuleContainer title="Theme Exposure" subtitle={`${themes.length} theme${themes.length === 1 ? '' : 's'} with holdings`}>
      <div className="space-y-1">
        {themes.slice(0, 8).map(t => (
          <div key={t.theme} className="flex items-center gap-2 text-[10px] md:text-[11px] font-mono py-1 border-b border-korinth-border/30 last:border-0">
            <span className="text-white flex-1 truncate">{t.theme}</span>
            <span className="text-korinth-accent font-bold w-12 text-right">{t.your_weight_pct.toFixed(0)}%</span>
            <span className="text-korinth-muted w-12 text-right">WL {t.watchlist_count}</span>
          </div>
        ))}
      </div>
    </ModuleContainer>
  );
}

function RecentAlertsBlock({ summary, domain }) {
  if (!summary) return null;
  return (
    <a href={`/alerts?domain=${domain}`} className="block bg-korinth-surface border border-korinth-border rounded-lg px-3 py-3 hover:border-korinth-accent/50 transition-colors" style={{ minHeight: 44 }}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Bell className="w-3.5 h-3.5 text-korinth-accent" />
          <span className="text-[10px] md:text-[11px] font-mono uppercase tracking-wider text-korinth-muted">Recent alerts · this domain</span>
        </div>
        <div className="flex gap-3 text-[10px] md:text-[11px] font-mono">
          <span className="text-white"><b>{summary.last_24h}</b> <span className="text-korinth-muted">24h</span></span>
          <span className="text-white"><b>{summary.last_7d}</b> <span className="text-korinth-muted">7d</span></span>
          {summary.critical_unresolved > 0 && (
            <span className="flex items-center gap-1 text-korinth-danger font-bold">
              <AlertTriangle className="w-3 h-3" /> {summary.critical_unresolved} critical
            </span>
          )}
          <span className="text-korinth-accent">View →</span>
        </div>
      </div>
    </a>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-14 bg-korinth-surface rounded-lg" />
      <div className="h-20 bg-korinth-surface rounded-lg" />
      <div className="h-40 bg-korinth-surface rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-32 bg-korinth-surface rounded-lg" />
        <div className="h-32 bg-korinth-surface rounded-lg" />
      </div>
    </div>
  );
}

// ─── Main template component ─────────────────────────────────────────────

export default function CommandCenterLayout({ domain, title, icon, subtitle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sprint 21.7.2 followup #2: tracks whether we're waiting on a
  // fallback /api/kairos/framing/:domain call to replace a stub.
  const [framingFallbackInFlight, setFramingFallbackInFlight] = useState(false);

  async function fetchFramingFallback() {
    // Called when the overview response came back with kairos_framing._stub:true.
    // Hits the standalone framing endpoint directly — no 5s timeout there, so
    // cold Sonnet calls complete. Merges into page state when ready.
    setFramingFallbackInFlight(true);
    try {
      const r = await fetch(`/api/kairos/framing/${domain}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const framing = await r.json();
      // Merge even if still stub (state will fall through to the
      // "Framing temporarily unavailable" render once fallbackInFlight=false)
      setData(prev => prev ? { ...prev, kairos_framing: framing } : prev);
    } catch {
      // Silent — component will show "unavailable" after fallbackInFlight flips
    }
    setFramingFallbackInFlight(false);
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/command/${domain}/overview`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const payload = await r.json();
      setData(payload);
      // Fire fallback if the primary overview returned stub framing
      if (payload?.kairos_framing?._stub) {
        fetchFramingFallback();
      }
    } catch (e) {
      setError(e.message || 'Failed to load Command Center');
    }
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [domain]);

  if (loading && !data) return <LoadingSkeleton />;
  if (error) {
    return (
      <div className="bg-korinth-surface border-l-4 border-l-korinth-danger border border-korinth-border rounded-lg p-4">
        <div className="text-sm font-mono text-korinth-danger font-bold mb-1">Command Center error</div>
        <p className="text-[11px] font-mono text-korinth-muted">{error}</p>
        <button onClick={load} className="mt-2 text-[10px] font-mono px-3 py-1 rounded border border-korinth-border hover:border-korinth-accent/50">Retry</button>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-korinth-accent/10 border border-korinth-accent/30 flex items-center justify-center text-korinth-accent">
            {icon}
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">{title}</h1>
            {subtitle && <p className="text-[10px] md:text-xs font-mono text-korinth-muted">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-muted">
          <Clock className="w-3 h-3" /> {fmtTime(data.as_of)}
        </div>
      </div>

      {/* Summary Strip */}
      <SummaryStrip strip={data.summary_strip} />

      {/* KAIROS Framing */}
      <KairosFraming framing={data.kairos_framing} domain={domain} fallbackInFlight={framingFallbackInFlight} />

      {/* Sprint 21.9: MONETARIS specialist — monetary regime card (macro only) */}
      {data.monetary_context && <MonetaryRegimeBlock ctx={data.monetary_context} />}

      {/* Sprint 21.10: AGORIS specialist — equity regime card (equity only) */}
      {data.equity_context && <EquityRegimeBlock ctx={data.equity_context} />}

      {/* Sprint 21.11: JANUS specialist — volatility regime card (options only) */}
      {data.volatility_context && <VolatilityRegimeBlock ctx={data.volatility_context} />}

      {/* Signals */}
      <SignalsBlock signals={data.signals} />

      {/* Your Exposure / Watchlist Candidates — side-by-side desktop, stacked mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <YourExposureBlock exposure={data.your_exposure} />
        <WatchlistCandidatesBlock candidates={data.watchlist_candidates} />
      </div>

      {/* Catalysts / Theme Exposure */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <CatalystsBlock catalysts={data.catalysts_upcoming} />
        <ThemeExposureBlock themes={data.theme_exposure} />
      </div>

      {/* Recent alerts summary */}
      <RecentAlertsBlock summary={data.recent_alerts_summary} domain={domain} />
    </div>
  );
}
